import { useRef, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { AnimatePresence, Transition, Variants } from 'framer-motion';
import { FiGlobe } from 'react-icons/fi';

import { useToggleOutsideClick, useLanguages } from '@hooks';
import { mapFromApiCode } from '@services/languages';

import FlagIcon from './FlagIcon';
import * as S from './styles';

const variants: Variants = {
  hidden: { opacity: 0, y: -4, scale: 0.95 },
  shown: { opacity: 1, y: 0, scale: 1 },
};

const transition: Transition = {
  type: 'spring',
  duration: 0.25,
  bounce: 0.1,
};

const DropdownLang = () => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const { languages, isLoading } = useLanguages();
  const [selectedLang, setSelectedLang] = useState('br');

  const [isDropdownVisible, toggleDropdownVisibility] = useToggleOutsideClick(
    dropdownRef,
    false,
    { handleInsideClick: true },
  );

  const changeLanguage = (langCode: string) => {
    const legacyCode = mapFromApiCode(langCode);
    setSelectedLang(legacyCode);
    i18n.changeLanguage(legacyCode);
  };

  const handleClick = isDropdownVisible ? undefined : toggleDropdownVisibility;

  useEffect(() => {
    if (localStorage.getItem('i18nextLng')) {
      const lng = localStorage.getItem('i18nextLng') || 'br';
      setSelectedLang(lng);
    }
  }, [setSelectedLang]);

  const currentLanguage = languages.find(
    (lang) => mapFromApiCode(lang.code) === selectedLang,
  );

  if (isLoading || languages.length === 0) {
    return null;
  }

  return (
    <S.Container ref={dropdownRef}>
      <S.Trigger type="button" $open={isDropdownVisible} onClick={handleClick}>
        <FiGlobe />
        <S.CurrentFlag>
          <FlagIcon code={currentLanguage?.code || ''} size={14} />
        </S.CurrentFlag>
      </S.Trigger>

      <AnimatePresence>
        {isDropdownVisible && (
          <S.Dropdown
            variants={variants}
            transition={transition}
            initial="hidden"
            animate="shown"
            exit="hidden"
          >
            {languages.map((lang) => {
              const isActive = mapFromApiCode(lang.code) === selectedLang;
              const code = lang.code.split('-')[0].toUpperCase();
              return (
                <S.Option
                  key={lang.id}
                  type="button"
                  $active={isActive}
                  onClick={() => changeLanguage(lang.code)}
                >
                  <FlagIcon code={lang.code} size={20} />
                  {lang.native_name.replace(/\s*\(.*\)/, '')}
                  <S.OptionCode $active={isActive}>{code}</S.OptionCode>
                </S.Option>
              );
            })}
          </S.Dropdown>
        )}
      </AnimatePresence>
    </S.Container>
  );
};

export default DropdownLang;
